import { forIn, isEqual, map, noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { filterToValidAttributes } from '@zocdoc/dom-property';

import { ZOCDOC, OTHER } from './mapStylers';
import { white } from '@zocdoc/branding/colors';
import loadGoogleMapsApi from '../utils/loadGoogleMapsApi';

const ENTITIES_KEY = 'entities';

const POSITION = {
    TOP_LEFT: 1,
    TOP_RIGHT: 2,
    BOTTOM_RIGHT: 3,
    BOTTOM_LEFT: 4,
};

/**
 * When using GoogleMap, make sure the eventual callsite is wrapped in a withTheme() HOC if you want automatic
 * white-labelling support. Do not use the HOC on interim components though. For reference, patient-web-application's
 * SearchMap uses withTheme in its index file where it is exported out to the world, but not in the component itself
 * where it extends GoogleMap.
 *
 * This is due to a limitation of how withTheme() is implemented, because it is a simple HOC
 * instead of acting more like a decorator.
 */
class GoogleMap extends PureComponent {
    static propTypes = {
        controlPosition: PropTypes.oneOf([
            POSITION.TOP_LEFT,
            POSITION.TOP_RIGHT,
            POSITION.BOTTOM_RIGHT,
            POSITION.BOTTOM_LEFT,
        ]),

        [ENTITIES_KEY]: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                location: PropTypes.shape({
                    latitude: PropTypes.number.isRequired,
                    longitude: PropTypes.number.isRequired,
                }).isRequired,
            })
        ),

        defaultLocation: PropTypes.shape({
            latitude: PropTypes.number.isRequired,
            longitude: PropTypes.number.isRequired,
        }),

        maxZoomLevel: PropTypes.number,

        onClickMarker: PropTypes.func,

        /**
         * When the map's bounds change for any reason, this function will be called with a
         * google ["LatLngBounds" object] (https://developers.google.com/maps/documentation/javascript/3.exp/reference#LatLngBounds).
         *
         * You can call a variety of methods on this object (like `getNorthEast()` and `getSouthWest()`) to construct
         * a bounding box and forward it to search service to get a new set of matching entity+location objects.
         */
        onMapBoundsChange: PropTypes.func,

        /**
         * When the user pans the map, this function will be called with a google ["LatLngBounds" object]
         * (https://developers.google.com/maps/documentation/javascript/3.exp/reference#LatLngBounds).
         *
         * You can call a variety of methods on this object (like `getNorthEast()` and `getSouthWest()`) to construct
         * a bounding box and forward it to search service to get a new set of matching entity+location objects.
         */
        onMapDragEnd: PropTypes.func,

        /**
         * This is called specifically in response to a user-initiated zoom via the map controls.
         *
         * Internal zooms can happen when "entities" change if the map must zoom out to accomodate the new set
         * and the primary onMapZoom() callback would be invoked for that particular use case.
         *
         * When the user zooms the map, this function will be called with a google ["LatLngBounds" object]
         * (https://developers.google.com/maps/documentation/javascript/3.exp/reference#LatLngBounds).
         *
         * You can call a variety of methods on this object (like `getNorthEast()` and `getSouthWest()`) to construct
         * a bounding box and forward it to search service to get a new set of matching entity+location objects.
         */
        onMapExplicitZoom: PropTypes.func,

        /**
         * This is called any time the map is zoomed, either for internal reasons or due to the user
         * clicking the zoom controls on the map.
         *
         * When the user zooms the map, this function will be called with a google ["LatLngBounds" object]
         * (https://developers.google.com/maps/documentation/javascript/3.exp/reference#LatLngBounds).
         *
         * You can call a variety of methods on this object (like `getNorthEast()` and `getSouthWest()`) to construct
         * a bounding box and forward it to search service to get a new set of matching entity+location objects.
         */
        onMapZoom: PropTypes.func,

        selectedEntityId: PropTypes.string,

        /**
         * This should be provided as a prop, probably via the withTheme() HOC from styled-components.
         */
        theme: PropTypes.shape({
            components: PropTypes.shape({
                GoogleMap: PropTypes.shape({
                    markerColor: PropTypes.string.isRequired,
                    markerColorHover: PropTypes.string.isRequired,
                    markerColorActive: PropTypes.string.isRequired,
                    useZocdocMapStyle: PropTypes.bool,
                }).isRequired,
            }).isRequired,
        }).isRequired,
    };

    static defaultProps = {
        controlPosition: POSITION.TOP_LEFT,
        defaultLocation: {
            latitude: 40.724313,
            longitude: -73.997337,
        },
        [ENTITIES_KEY]: [],
        maxZoomLevel: 16,
        onMapBoundsChange: noop,
        onMapDragEnd: noop,
        onMapExplicitZoom: noop,
        onMapZoom: noop,
    };

    static generatePin = (fillColor, strokeColor = white) => ({
        // the icon is 26wide by 40 tall, so we set X to 13 so it is centered
        anchor: new window.google.maps.Point(13, 40),
        fillColor,
        fillMode: 'evenodd',
        fillOpacity: 1,
        path: 'M11.7,39.6L2.6,20.9C1.9,19.3.5,16.3.5,13,.5,6.1,6.1.5,13,.5s12.5,5.6,12.5,12.6c0,3.3-1.3,6.1-2,7.8l-9.2,18.7h-2.6zm1.3-32c-3,0-5.5,2.5-5.5,5.5s2.5,5.5,5.5,5.5,5.5-2.5,5.5-5.5S16,7.6,13,7.6',
        scale: 1,
        strokeColor,
        strokeWeight: 0.5,
    });

    static getGoogleControlPosition = position => {
        switch (position) {
            case POSITION.TOP_RIGHT:
                return window.google.maps.ControlPosition.TOP_RIGHT;
            case POSITION.BOTTOM_RIGHT:
                return window.google.maps.ControlPosition.BOTTOM_RIGHT;
            case POSITION.BOTTOM_LEFT:
                return window.google.maps.ControlPosition.BOTTOM_LEFT;
            default:
                return window.google.maps.ControlPosition.TOP_LEFT;
        }
    };

    placeholderRef = React.createRef();

    activePin = null;
    hoverPin = null;
    inactivePin = null;
    initialZoomComplete = false;
    map = null;
    mapApiPromise = null;
    markers = {};
    shouldFireUserZoomCallback = true;

    async componentDidMount() {
        await loadGoogleMapsApi();
        await this.createMap();
    }

    didEntitiesChange(v, o) {
        // do a depth-1 ID compare on entities, but a shallow compare on everything else
        // it's slightly more expensive upfront, but recreating the markers unnecessarily makes
        // the map flash which is unsightly

        if ((v[ENTITIES_KEY] || o[ENTITIES_KEY]) && !isEqual(map(v[ENTITIES_KEY], 'id'), map(o[ENTITIES_KEY], 'id'))) {
            return true;
        }

        return false;
    }

    componentDidUpdate(prevProps) {
        if (this.didEntitiesChange(prevProps, this.props)) {
            /**
             * When "entities" changes, an internal zoom can occur as a result of the map being recentered
             * on the new set of markers. This is considered an "internal" zoom event since it is not being
             * triggered by the user interacting with the map controls.
             */
            this.shouldFireUserZoomCallback = false;

            this.createMarkers();
            return;
        }

        if (this.markersByEntityLocationId) {
            const nextSelectedMarker = this.markersByEntityLocationId[this.props.selectedEntityId];
            const prevSelectedMarker = this.markersByEntityLocationId[prevProps.selectedEntityId];

            if (nextSelectedMarker !== prevSelectedMarker) {
                if (nextSelectedMarker) {
                    nextSelectedMarker.selected = true;
                    nextSelectedMarker.setIcon(this.activePin);
                }
                if (prevSelectedMarker) {
                    nextSelectedMarker.selected = false;
                    prevSelectedMarker.setIcon(this.inactivePin);
                }
            }
        }
    }

    componentWillUnmount() {
        if (this.mapBoundsListener) {
            window.google.maps.event.removeListener(this.mapBoundsListener);
        }

        if (this.mapDragListener) {
            window.google.maps.event.removeListener(this.mapDragListener);
        }

        if (this.mapZoomListener) {
            window.google.maps.event.removeListener(this.mapZoomListener);
        }

        this.clearMarkers();

        window.google.maps.event.clearInstanceListeners(document);
        window.google.maps.event.clearInstanceListeners(window);

        this.map = null;
    }

    render() {
        return (
            <figure
                data-test="google-map"
                {...filterToValidAttributes(this.props)}
                ref={this.placeholderRef}
            />
        );
    }

    createMap = () => new Promise(resolve => {
        const theme = this.props.theme.components.GoogleMap;

        // needs the gmaps Point API 🙄
        this.activePin = GoogleMap.generatePin(theme.markerColorActive);
        this.hoverPin = GoogleMap.generatePin(theme.markerColorHover);
        this.inactivePin = GoogleMap.generatePin(theme.markerColor);

        this.map = new window.google.maps.Map(this.placeholderRef.current, {
            fullscreenControl: false,
            mapTypeControl: false,
            rotateControl: false,
            scaleControl: false,
            scrollwheel: false,
            streetViewControl: false,
            styles: theme.useZocdocMapStyle ? ZOCDOC : OTHER,
            zoomControl: true,
            zoomControlOptions: {
                position: GoogleMap.getGoogleControlPosition(this.props.controlPosition),
            },
        });

        this.mapBoundsListener = window.google.maps.event.addListener(this.map, 'bounds_changed', () => {
            this.props.onMapBoundsChange(this.map.getBounds());
        });

        this.mapDragListener = window.google.maps.event.addListener(this.map, 'dragend', () => {
            this.props.onMapDragEnd(this.map.getBounds());
        });

        this.mapZoomListener = window.google.maps.event.addListener(this.map, 'zoom_changed', () => {
            if (this.initialZoomComplete) this.props.onMapZoom(this.map.getBounds());
            if (this.shouldFireUserZoomCallback) this.props.onMapExplicitZoom(this.map.getBounds());
        });

        this.createMarkers();

        window.google.maps.event.addListenerOnce(this.map, 'idle', () => {
            const zoom = this.map.getZoom();
            if (!zoom || zoom > this.props.maxZoomLevel) this.map.setZoom(this.props.maxZoomLevel);

            /**
             * We don't want the consumer to be bothered about the first zoom which is essentially
             * just setting up the initial state of the map to our defaults.
             */
            this.initialZoomComplete = true;

            resolve();
        });
    });

    clearMarkers() {
        forIn(this.markers, marker => {
            window.google.maps.event.clearInstanceListeners(marker);
            marker.setMap(null);
        });

        this.markers = {};
        this.markersByEntityLocationId = {};
    }

    createMarkers(props = this.props) {
        if (!this.map) { return; }

        this.clearMarkers();

        const entities = props[ENTITIES_KEY];

        if (entities.length) {
            const bounds = new window.google.maps.LatLngBounds();

            entities.forEach(({ id, location }) => {
                const icon = id === props.selectedEntityId ? this.activePin : this.inactivePin;
                const latlngKey = `${location.latitude},${location.longitude}`;

                if (!this.markers[latlngKey]) {
                    const latlng = new window.google.maps.LatLng(location.latitude, location.longitude);
                    const marker = new window.google.maps.Marker({
                        icon,
                        map: this.map,
                        position: latlng,
                        selected: props.selectedEntityId === id,
                    });

                    marker.addListener('click', () => {
                        this.onClickMarker(latlngKey);
                    });

                    marker.addListener('dblclick', () => {
                        this.map.setCenter(latlng);
                        this.map.setZoom(props.maxZoomLevel);
                    });

                    marker.addListener('mouseout', () => {
                        if (marker.selected) {
                            marker.setIcon(this.activePin);
                        } else {
                            marker.setIcon(this.inactivePin);
                        }
                    });
                    marker.addListener('mouseover', () => {
                        if (!marker.selected) {
                            marker.setIcon(this.hoverPin);
                        }
                    });

                    bounds.extend(latlng);

                    this.markers[latlngKey] = marker;
                }

                this.markersByEntityLocationId[id] = this.markers[latlngKey];
            });

            this.recenterMap(bounds, props);
        } else {
            this.resetMap(props);
        }

        /**
         * Put everything back to normal now that the map is ready to go.
         */
        this.shouldFireUserZoomCallback = true;
    }

    resetMap(props = this.props) {
        this.map.setCenter(
            new window.google.maps.LatLng(
                props.defaultLocation.latitude,
                props.defaultLocation.longitude
            )
        );

        this.map.setZoom(props.maxZoomLevel);
    }

    recenterMap(bounds, props = this.props) {
        this.map.fitBounds(bounds);
        this.map.panToBounds(bounds);

        if (this.map.getZoom() > props.maxZoomLevel) {
            this.map.setZoom(props.maxZoomLevel);
        }
    }

    onClickMarker() {}

    triggerMarkerEvent = (type, targetLocationId) => {
        // ignore calls before the map has finished initializing
        if (!this.markersByEntityLocationId) { return; }

        if (this.markersByEntityLocationId[targetLocationId]) {
            window.google.maps.event.trigger(this.markersByEntityLocationId[targetLocationId], type);
        }
    };
}

export {
    GoogleMap as default,
    GoogleMap,
    POSITION,
};
